import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Image as ImageIcon, Send, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

function wsUrl(bookingId, token) {
  const proto = BASE.startsWith("https") ? "wss" : "ws";
  const host = BASE.replace(/^https?:\/\//, "");
  return `${proto}://${host}/api/ws/chat/${bookingId}?token=${encodeURIComponent(token || "")}`;
}

// -----------------------------------------------------------------------
// ChatPanel — real-time WebSocket chat for a booking. Works for admin,
// astrologer, and client (paid) as long as their token/session matches.
// -----------------------------------------------------------------------
export default function ChatPanel({ bookingId, token, title = "Live Chat", onClose }) {
  const [messages, setMessages] = useState([]);
  const [me, setMe] = useState(null);
  const [err, setErr] = useState("");
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const wsRef = useRef(null);
  const listRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/chat/${bookingId}/history`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {},
            withCredentials: true })
      .then(r => {
        if (cancelled) return;
        setMessages(r.data?.messages || []);
        setMe(r.data?.me || null);
      })
      .catch(ex => setErr(ex?.response?.data?.detail || "Chat not available"));

    const ws = new WebSocket(wsUrl(bookingId, token));
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const m = JSON.parse(ev.data);
        if (m.type === "message") setMessages(ms => [...ms, m]);
        else if (m.type === "joined") setMe(m.me);
        else if (m.type === "error") setErr(m.message || "Chat error");
      } catch {}
    };
    ws.onerror = () => setErr("Live socket dropped — messages sent while offline will not reach the peer.");
    ws.onclose = (ev) => {
      if (ev.code === 4401 || ev.code === 4403) setErr("Not authorised for this room");
    };
    return () => { cancelled = true; try { ws.close(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const send = (payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) { setErr("Live socket not open"); return; }
    ws.send(JSON.stringify(payload));
  };

  const sendText = (e) => {
    e?.preventDefault?.();
    if (!text.trim()) return;
    send({ text: text.trim() });
    setText("");
  };

  const pickImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/chat/${bookingId}/upload`, fd, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      });
      send({ image_url: r.data.image_url });
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Image upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="border border-[#D4AF37]/40 rounded-sm bg-[#0F1A33]/70 flex flex-col h-[70vh]"
         data-testid="chat-panel">
      <div className="flex items-center justify-between border-b border-[#2A3555] px-3 py-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] font-mono text-[#D4AF37]">
            Astro-Connect · Paid Chat
          </div>
          <div className="font-heading text-[#E9E4D0] text-sm">
            {title} · <span className="text-[#8B99B0] font-mono text-xs">{bookingId}</span>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-[#8B99B0] hover:text-red-400 p-1"
                  data-testid="chat-close">
            <X size={16} />
          </button>
        )}
      </div>
      <div ref={listRef} data-testid="chat-messages"
           className="flex-1 overflow-y-auto p-3 space-y-2 text-sm">
        {messages.length === 0 && (
          <div className="text-xs font-mono text-[#8B99B0] italic text-center pt-8">
            {err || "No messages yet — say hello."}
          </div>
        )}
        {messages.map((m) => {
          const mine = me && m.sender_id === me.id && m.sender_role === me.role;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-sm px-3 py-2 border
                  ${mine ? "bg-[#D4AF37]/10 border-[#D4AF37]/40 text-[#E9E4D0]"
                           : "bg-[#0A1428] border-[#2A3555] text-[#E9E4D0]"}`}>
                <div className="text-[10px] uppercase tracking-widest font-mono text-[#D4AF37]">
                  {m.sender_name} · {m.sender_role}
                </div>
                {m.text && <div className="mt-1 whitespace-pre-wrap">{m.text}</div>}
                {m.image_url && (
                  <a href={`${BASE}${m.image_url}`} target="_blank" rel="noreferrer noopener">
                    <img src={`${BASE}${m.image_url}`} alt="shared"
                          className="mt-2 max-h-56 rounded-sm border border-[#D4AF37]/30" />
                  </a>
                )}
                <div className="text-[9px] font-mono text-[#8B99B0] mt-1">
                  {new Date(m.ts).toLocaleTimeString()}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {err && <div className="text-red-400 text-xs font-mono px-3" data-testid="chat-err">{err}</div>}
      <form onSubmit={sendText} className="flex items-center gap-2 border-t border-[#2A3555] p-2">
        <label className="cursor-pointer border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 p-2 rounded-sm">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
          <input ref={fileRef} type="file" accept="image/*"
                   onChange={(e) => pickImage(e.target.files?.[0])}
                   data-testid="chat-image-input" className="hidden" />
        </label>
        <input value={text} onChange={(e) => setText(e.target.value)}
                 placeholder="Type a message…" data-testid="chat-text"
                 className="flex-1 bg-[#0A1428] border border-[#2A3555] focus:border-[#D4AF37] text-[#E9E4D0] font-mono text-sm px-3 py-2 rounded-sm" />
        <button type="submit" disabled={!text.trim()} data-testid="chat-send-btn"
                  className="bg-gradient-to-r from-[#B08A3E] to-[#E5C158] text-[#0A1428] font-mono text-xs uppercase tracking-widest px-3 py-2 rounded-sm disabled:opacity-50 flex items-center gap-1">
          <Send size={12} /> Send
        </button>
      </form>
    </div>
  );
}
